import { KalturaRuleType } from '@kaltura-ott/ott-client-types/api/types/KalturaRuleType';
import {
  KuxFilteredCategoriesDTO,
  LCBatchesPresentationOrderEnum,
  PERFORMANCE_FEATURE,
  UIDAPresentationTypeEnum,
  addChannelStats,
  AssetPresentationRatioEnum,
  getSystemNameById,
  SecondButtonActions,
} from '@kaltura-ott/tvpil';
import { computed, action, reaction, IReactionDisposer, observable } from 'mobx';
import { container } from 'tsyringe';

import { fetchChannelCollection } from '../../../../../rhino';
import { queryClient } from '../../../../../rhino/data/queryClient';
import { queryKeys } from '../../../../../rhino/data/queryKeys';
import { featureEnabler } from '../../../../../utils';
import { errorHandling } from '../../../../../utils/error-handling';
import { sortByOrderBatches } from '../../../../../utils/sorting';
import { channelStats } from '../../../../channelStats/channel-stats-store';
import { LastWatchedLinearStore } from '../../../../channelStats/last-watched-linear-store';
import { DmsStore } from '../../../../dms';
import { CardPlaceholder, Linear } from '../../../../entities';
import { addCurrentProgramToLinear } from '../../../../entities/entities.service';
import { ErrorPriority } from '../../../../error';
import type { FavoriteRepository } from '../../../../favoriteRepository';
import type { LinearRepository } from '../../../../linearRepository/linear-repository';
import type { TransactionStore } from '../../../../transaction/transaction-store';
import { RailStoreV2 } from '../rail-store-v2';

export class HeroRailStoreV2 extends RailStoreV2<Linear> {
  static PERFORMANCE_LAST_WATCHED = true;
  static moduleName = 'HeroRailStoreV2';

  subtype: UIDAPresentationTypeEnum.hero = UIDAPresentationTypeEnum.hero;

  @observable protected innerItems: Linear[] = [];
  private allLinear: Linear[] = [];

  protected _removeInvisible = false;

  private presentCurrentPrograms = false;
  private interval?: NodeJS.Timeout;
  private followFirstProgramTimer?: NodeJS.Timeout;
  private mostWatchedParams: KuxFilteredCategoriesDTO['mostWatched'];
  private orderBatches: LCBatchesPresentationOrderEnum[] = [LCBatchesPresentationOrderEnum.lcn];
  private programRefreshFrequency = 0;
  private lastWatchedLinear: LastWatchedLinearStore;
  private followFirstProgramDisposer: IReactionDisposer | undefined;
  private transactionStore: TransactionStore;
  private channelPresentationImageType: string;
  @observable private visible = false;
  @observable private currentFavVersionNumber?: number;

  public secondButtonAction: SecondButtonActions;

  constructor(props: KuxFilteredCategoriesDTO, parentCategoryTree: KuxFilteredCategoriesDTO, children?: []) {
    super(props, parentCategoryTree, children);

    this.lastWatchedLinear = container.resolve<LastWatchedLinearStore>('lastWatchedLinearStore');

    if (props.epgProperty.orderBatches && props.epgProperty.orderBatches.length) {
      this.orderBatches = props.epgProperty.orderBatches;
    }
    if (props.railProperty.presentCurrentPrograms !== false && props.railProperty.programRefreshFrequency) {
      this.presentCurrentPrograms = true;
      this.programRefreshFrequency = props.railProperty.programRefreshFrequency;
    }
    this.channelPresentationImageType =
      // @ts-expect-error TODO Fix
      getSystemNameById(props.railImageProperty.channelPresentationImageType) || AssetPresentationRatioEnum['16x9'];

    this.mostWatchedParams = props.mostWatched;

    this.transactionStore = container.resolve<TransactionStore>('transactionStore');
    this.secondButtonAction = props.railProperty.secondButtonAction || SecondButtonActions.details_page;
  }

  private async updatePrograms() {
    const dmsStore = container.resolve<DmsStore>('dmsStore');
    await addCurrentProgramToLinear(
      this.items.slice(1),
      undefined,
      undefined,
      dmsStore.dms.mosaicInfo.isMosaicSupported,
    );
  }

  @errorHandling({
    type: ErrorPriority.low,
    callback: function (this: HeroRailStoreV2) {
      this.errorHandlerCallback();
    },
  })
  async init(preload = false) {
    if (!this.visible) {
      const favoriteRepository = container.resolve<FavoriteRepository>('favoriteRepository');
      await favoriteRepository.getFavoritesList();
      this.sortChannels();
    }

    if (!preload) {
      this.lastWatchedLinear.setCurrentHeroRail(this.pageReferenceId);
      this.followPrograms();
      this.visible = true;
    }

    return super.init();
  }

  @computed
  public get isReady(): boolean {
    // @ts-expect-error TODO Fix
    return (
      this._isReady &&
      (this.lastWatchedLinear.isReady || this.lastWatchedLinear.getLastLinear(this.pageReferenceId)) &&
      this.visible
    );
  }

  @computed
  public get lastWatchedSubLinearExternalId(): string | undefined {
    return this.lastWatchedLinear.getLastLinear(this.pageReferenceId)?.subLinearExternalId;
  }

  @computed
  public get lastWatchedSubProgramExternalId(): string | undefined {
    return this.lastWatchedLinear.getLastLinear(this.pageReferenceId)?.subProgramExternalId;
  }

  @errorHandling({
    callback: function (this: HeroRailStoreV2) {
      this.errorHandlerCallback();
    },
  })
  @computed
  public get items(): Linear[] {
    if (HeroRailStoreV2.PERFORMANCE_LAST_WATCHED) {
      const lastWatchedEntry = this.lastWatchedLinear.getLastLinear(this.pageReferenceId);
      const lastWatchedLinear = lastWatchedEntry?.linear;
      if ((!this.lastWatchedLinear.isReady && !lastWatchedLinear) || !this.innerItems.length) return [];
      if (lastWatchedLinear) {
        // put lastWatched item first
        const isLastWatchedGeoBlocked = !!lastWatchedLinear.getRuleByType(KalturaRuleType.geo);
        if (lastWatchedLinear.isPurchased && !isLastWatchedGeoBlocked) {
          const lastWatchedLinearIndex = this.innerItems.findIndex((linear) => linear.id === lastWatchedLinear.id);

          // if no Linear item in list just add it to the beginning
          if (lastWatchedLinearIndex === -1) {
            return [lastWatchedLinear, ...this.innerItems].slice(0, this.opcItemsLimit);
          }

          // if Linear item is already in the list cut it out and place it first
          return [
            lastWatchedLinear,
            ...this.innerItems.slice(0, lastWatchedLinearIndex),
            ...this.innerItems.slice(lastWatchedLinearIndex + 1),
          ];
        }
      }
    }
    return this.innerItems;
  }

  public get channelImageType() {
    return this.channelPresentationImageType;
  }

  @action
  async getData(
    pageIndex: number,
    items: Linear[],
  ): Promise<{ items: Linear[]; totalCount: number; newItems: Linear[] }> {
    const linearsData = await queryClient.fetchQuery({
      queryKey: queryKeys.channelCollection(
        this.channels[0],
        this.transactionStore.isAdultEntitled,
        this.aggregatedKSQL,
      ),
      queryFn: () =>
        fetchChannelCollection({
          collectionId: this.channels[0],
          includeAdult: this.transactionStore.isAdultEntitled,
          kSql: this.aggregatedKSQL,
        }),
      staleTime: Number.MAX_SAFE_INTEGER,
    });

    const linearRepository = container.resolve<LinearRepository>('linearRepository');
    const watcheStats = channelStats.getMostWatched(
      this.mostWatchedParams.presentMostWatchedPeriod,
      this.mostWatchedParams.presentMostWatchedCount,
    );

    this.allLinear = linearsData.objects
      .map(({ id }) => {
        const linear = linearRepository.getLinearById(id);

        if (!linear) {
          return;
        }

        addChannelStats(linear, watcheStats);

        return linear;
      })
      .filter(linearRepository.isLinear);

    this.sortChannels(this.allLinear);

    if (HeroRailStoreV2.PERFORMANCE_LAST_WATCHED) {
      const lastWatchedEntry = this.lastWatchedLinear.getLastLinear(this.pageReferenceId);
      const lastWatchedLinear = lastWatchedEntry?.linear;

      if (lastWatchedLinear) {
        const dmsStore = container.resolve<DmsStore>('dmsStore');
        await addCurrentProgramToLinear(
          [lastWatchedLinear],
          undefined,
          true,
          dmsStore.dms.mosaicInfo.isMosaicSupported,
        );
      }
    }

    return { items: this.items, totalCount: this.innerItems.length, newItems: this.innerItems as Linear[] };
  }

  private sortChannels(items = this.allLinear) {
    const favoriteRepository = container.resolve<FavoriteRepository>('favoriteRepository');
    if (
      this.innerItems.length &&
      (!this.orderBatches.includes(LCBatchesPresentationOrderEnum.fav) ||
        favoriteRepository.versionNumber === this.currentFavVersionNumber)
    ) {
      return;
    }

    this.innerItems = sortByOrderBatches(items, this.orderBatches).slice(0, this.variableItemsLimit);

    this.currentFavVersionNumber = favoriteRepository.versionNumber;
  }

  protected async getAdditionData(itemsInPage: Linear[]): Promise<void> {
    const dmsStore = container.resolve<DmsStore>('dmsStore');
    await addCurrentProgramToLinear(itemsInPage, undefined, undefined, dmsStore.dms.mosaicInfo.isMosaicSupported);
    return undefined;
  }

  protected async restoreAdditionData(pageIndex: number, items: (CardPlaceholder | Linear)[]): Promise<void> {
    return undefined;
  }

  private followFirstProgram() {
    if (
      featureEnabler.variation(
        'PERFORMANCE_FOLLOW_FIRST_PROGRAM_HERO',
        PERFORMANCE_FEATURE.PERFORMANCE_FOLLOW_FIRST_PROGRAM_HERO,
      )
    ) {
      this.disposeFollowFirstProgramReaction();
      this.followFirstProgramDisposer = reaction(
        () => this.items.length && this.items[0]?.currentProgram?.id, // check is program changed
        this.followFirstProgramReaction,
      );
    }
  }

  private followFirstProgramReaction = async () => {
    if (this.items.length && this.items[0]?.currentProgram?.data?.endDate) {
      this.unFollowFirstProgram();
      const timeout = this.items[0].currentProgram.data.endDate - Date.now() + 5000;
      this.followFirstProgramTimer = setTimeout(async () => {
        if (this.items[0]) {
          const dmsStore = container.resolve<DmsStore>('dmsStore');
          addCurrentProgramToLinear([this.items[0]], undefined, true, dmsStore.dms.mosaicInfo.isMosaicSupported);
          this.followFirstProgram();
        }
      }, timeout);
    }
  };

  private unFollowFirstProgram(): void {
    if (this.followFirstProgramTimer) clearTimeout(this.followFirstProgramTimer);
  }

  private disposeFollowFirstProgramReaction(): void {
    if (this.followFirstProgramDisposer) this.followFirstProgramDisposer();
  }

  private async followPrograms(): Promise<void> {
    if (this.interval) this.unFollowPrograms();
    if (this.programRefreshFrequency && this.presentCurrentPrograms) {
      this.interval = setInterval(() => this.updatePrograms(), this.programRefreshFrequency * 1000);
      this.followFirstProgram();
      this.followFirstProgramReaction();
    }
  }

  private unFollowPrograms(): void {
    this.destroy();
    this.unFollowFirstProgram();
    this.disposeFollowFirstProgramReaction();
  }

  onInvisible() {
    super.onInvisible();
    this.unFollowPrograms();
    this.visible = false;
  }

  /**
   * Destroy timer which is tracking programs for updating
   */
  public destroy() {
    if (this.interval) clearInterval(this.interval);
    this.visible = false;
  }
}
